// src/pages/common/reports/R0006/components/R0006KPISection.js

import React, { useState, useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import KPICard from 'components/common/cards/KPICard';
import { calculateKPIValues } from '../services/kpiService';
import { getDaysCount } from '../utils/dateUtils';
import { getClientKPIConfig } from '../utils/clientConfigUtils';

export default function R0006KPISection({
  selectedArea,
  dateRange,
  reportData,
  dataReady
}) {
  const [kpiValues, setKpiValues] = useState(null);
  const [kpiLoading, setKpiLoading] = useState(false);
  const [kpiError, setKpiError] = useState(null);

  // Effect to calculate KPI values when area or date range changes
  useEffect(() => {
    if (dataReady && selectedArea) {
      setKpiLoading(true);
      setKpiError(null);
      
      // Use setTimeout to simulate async calculation
      const timeoutId = setTimeout(() => {
        try {
          const daysCount = getDaysCount(dateRange);
          const calculatedValues = calculateKPIValues(selectedArea, daysCount, reportData);
          setKpiValues(calculatedValues);
          setKpiLoading(false);
        } catch (error) {
          console.error('KPI calculation error:', error);
          setKpiError('Failed to calculate KPI values');
          setKpiLoading(false);
        }
      }, 300);

      // Cleanup function
      return () => clearTimeout(timeoutId);
    }
  }, [dataReady, selectedArea, dateRange, reportData]);

  const getKPICardProps = (kpiType) => {
    // Get client-specific KPI configuration with error handling
    let kpiConfig;
    try {
      kpiConfig = getClientKPIConfig();
    } catch (error) {
      console.error('Failed to load KPI config:', error);
      // Use fallback config
      kpiConfig = getDefaultKPIConfig();
    }
    
    // Ensure kpiConfig has required structure
    if (!kpiConfig || !kpiConfig.themes) {
      console.warn('Invalid KPI config, using fallback');
      kpiConfig = getDefaultKPIConfig();
    }
    
    const configs = {
      totalSales: {
        title: "Total Sales",
        value: kpiValues?.totalSales,
        type: "currency",
        iconType: "money",
        iconColor: kpiConfig.themes?.totalSales?.iconColor || '#2196f3',
        // Add comparison data if available
        comparison: kpiValues?.totalSalesProjected ? {
          actual: kpiValues.totalSales,
          projected: kpiValues.totalSalesProjected
        } : null,
      },
      
      performance: {
        title: "Sales Performance",
        value: kpiValues?.performanceCount,
        type: "text",
        iconType: "check", 
        iconColor: kpiConfig.themes?.performance?.iconColor || '#4caf50',
        rating: kpiConfig.ratings?.performance // 3-line display with rating
      },
      
      opportunity: {
        title: "Opportunity",
        value: kpiValues?.opportunity,
        type: "text", 
        iconType: "trending",
        iconColor: kpiConfig.themes?.opportunity?.iconColor || '#ff9800',
        rating: kpiConfig.ratings?.opportunity, // 3-line display with rating
        ratingValue: kpiValues?.opportunityPercentage // Use percentage for rating calculation
      }
    };
    
    return configs[kpiType];
  };

  // Don't render anything if data is not ready
  if (!dataReady) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard 
            {...getKPICardProps('totalSales')}
            loading={kpiLoading}
            error={kpiError}
            compact
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <KPICard 
            {...getKPICardProps('performance')}
            loading={kpiLoading}
            error={kpiError}
            compact
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <KPICard 
            {...getKPICardProps('opportunity')}
            loading={kpiLoading}
            error={kpiError}
            compact
          />
        </Grid>
      </Grid>
    </Box>
  );
}

// Fallback KPI configuration in case client config fails
const getDefaultKPIConfig = () => {
  return {
    themes: {
      totalSales: { iconColor: '#2196f3' },
      performance: { iconColor: '#4caf50' },
      opportunity: { iconColor: '#ff9800' }
    },
    ratings: {
      performance: {
        excellent: { min: 90, color: '#2196f3', label: 'Excellent' },
        good: { min: 75, color: '#4caf50', label: 'Good' },
        fair: { min: 50, color: '#ff9800', label: 'Fair' },
        poor: { min: 0, color: '#f44336', label: 'Poor' }
      },
      opportunity: {
        ranges: [
          { min: 0,   max: 3.9, color: '#ffffff', bgColor: '#4caf50', label: 'Optimized' },
          { min: 4,   max: 10,  color: '#000000', bgColor: '#ff9800', label: 'Moderate' },
          { min: 10.1, max: 100, color: '#ffffff', bgColor: '#f44336', label: 'High Priority' }
        ]
      }
    }
  };
};